import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Separator } from '@cuemath/comps';
import {
  FacetBar as FacetBarWrap,
  FacetBtn,
  FacetPopover,
  AppliedChip,
} from '../../../styled-comps';
import {
  FacetBtnWrap,
  FacetOptionRow,
  FacetCheckbox,
  AppliedRow,
  ChipClose,
  ClearBtn,
  SaveBtn,
  SavePopover,
  SaveInput,
  SaveActions,
  SaveActionBtn,
  FacetBadge,
  FilterErrorMsg,
} from '../styled';
import Icon from '../../icon';

// Vertical offset between the facet button's bottom edge and the popover's top.
const POPOVER_BUTTON_GAP_PX = 4;
// Minimum gap kept between the popover's bottom edge and the viewport's bottom.
const POPOVER_VIEWPORT_PADDING_PX = 16;
// Minimum popover height even when the button sits near the viewport's bottom.
const POPOVER_MIN_HEIGHT_PX = 160;

const FacetBarSection = ({
  facets,
  applied,
  activeFacet,
  saving,
  savedName,
  savingFilter,
  filterError,
  facetAppliedCount,
  isFacetOptionApplied,
  onToggleFacet,
  onCloseFacet,
  onToggleOption,
  onRemoveApplied,
  onClearAll,
  onStartSave,
  onSaveNameChange,
  onCancelSave,
  onSubmitSave,
}) => {
  const btnRefs = useRef({});
  const popoverRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState(null);

  useLayoutEffect(() => {
    if (!activeFacet) {
      setPopoverPos(null);
      return undefined;
    }
    const compute = () => {
      const btn = btnRefs.current[activeFacet];
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const top = rect.bottom + POPOVER_BUTTON_GAP_PX;
      const maxHeight = Math.max(
        POPOVER_MIN_HEIGHT_PX,
        window.innerHeight - top - POPOVER_VIEWPORT_PADDING_PX,
      );
      setPopoverPos({ top, left: rect.left, maxHeight });
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, true);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute, true);
    };
  }, [activeFacet]);

  // Close popover on outside click (uses both popover and active-button refs
  // so we don't depend on DOM-tree containment for position:fixed children).
  useEffect(() => {
    if (!activeFacet || !onCloseFacet) return undefined;
    const handler = e => {
      const target = e.target;
      const btn = btnRefs.current[activeFacet];
      if (btn && btn.contains(target)) return;
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      onCloseFacet();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeFacet, onCloseFacet]);

  // Close popover on Escape
  useEffect(() => {
    if (!activeFacet || !onCloseFacet) return undefined;
    const handler = e => {
      if (e.key === 'Escape') onCloseFacet();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [activeFacet, onCloseFacet]);

  return (
    <>
      <FacetBarWrap>
        {facets.map(f => {
          const count = facetAppliedCount(f.key);
          return (
            <FacetBtnWrap key={f.key} gutter={0} title={f.disabledReason || undefined}>
              <FacetBtn
                ref={el => {
                  btnRefs.current[f.key] = el;
                }}
                disabled={f.disabled}
                onClick={() => onToggleFacet(f.key)}
              >
                <Icon name="filter" size={12} />
                {f.label}
                {count > 0 && <FacetBadge>{count}</FacetBadge>}
              </FacetBtn>
              {!f.disabled && activeFacet === f.key && popoverPos && (
                <FacetPopover
                  ref={popoverRef}
                  $top={popoverPos.top}
                  $left={popoverPos.left}
                  $maxHeight={popoverPos.maxHeight}
                >
                  {f.options.map(opt => {
                    const optValue = typeof opt === 'string' ? opt : opt.value;
                    const optLabel = typeof opt === 'string' ? opt : opt.label;
                    const checked = isFacetOptionApplied(f.key, optValue);
                    return (
                      <FacetOptionRow
                        key={optValue}
                        onClick={() => onToggleOption(f.key, optLabel, optValue)}
                      >
                        <FacetCheckbox $checked={checked}>
                          {checked && <Icon name="check" size={10} color="white" />}
                        </FacetCheckbox>
                        {optLabel}
                      </FacetOptionRow>
                    );
                  })}
                </FacetPopover>
              )}
            </FacetBtnWrap>
          );
        })}
      </FacetBarWrap>

      {applied.length > 0 && (
        <>
          <Separator height={8} transparent />
          <AppliedRow>
            {applied.map(a => (
              <AppliedChip key={`${a.facet}-${a.value}`}>
                {a.label}
                <ChipClose onClick={() => onRemoveApplied(a.facet, a.value)}>
                  <Icon name="close" size={10} />
                </ChipClose>
              </AppliedChip>
            ))}
            <ClearBtn onClick={onClearAll}>Clear</ClearBtn>
            <SaveBtn onClick={onStartSave} title="Save these filters as a view">
              <Icon name="save" size={11} />
            </SaveBtn>
          </AppliedRow>

          {saving && (
            <SavePopover>
              <SaveInput
                autoFocus
                placeholder="Name this view..."
                value={savedName}
                onChange={e => onSaveNameChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') onSubmitSave();
                }}
              />
              {filterError && <FilterErrorMsg>{filterError}</FilterErrorMsg>}
              <SaveActions>
                <SaveActionBtn onClick={onCancelSave}>Cancel</SaveActionBtn>
                <SaveActionBtn
                  $primary
                  disabled={!savedName.trim() || savingFilter}
                  onClick={onSubmitSave}
                >
                  {savingFilter ? 'Saving...' : 'Save view'}
                </SaveActionBtn>
              </SaveActions>
            </SavePopover>
          )}
        </>
      )}
    </>
  );
};

export default FacetBarSection;
